/*
 * Deadly Ponies: Book an Appointment drawer.
 *
 * A vanilla custom element, no build step, no libraries. Structure follows khaite.com's
 * booking drawer: title bar, thin progress line, numbered steps, and a fixed footer with
 * Back + Continue. Free times and bookings come from Cal.com's public (no key) API;
 * Klaviyo is told about each booking with the public company ID only. All wording and
 * site-specific values come from the JSON config the section renders inside the element:
 *
 *   <appointment-drawer>
 *     <script type="application/json" data-appointment-config>{ ... }</script>
 *   </appointment-drawer>
 *
 * Any link whose href ends in "#book-appointment", or any element with
 * [data-appointment-open], opens the drawer.
 */
(() => {
  if (customElements.get('appointment-drawer')) return;

  const CAL_API = 'https://api.cal.com/v2';
  const SLOTS_VERSION = '2024-09-04';
  const BOOKINGS_VERSION = '2026-02-25';
  const KLAVIYO_API = 'https://a.klaviyo.com/client';
  const KLAVIYO_REVISION = '2026-07-15';
  const NZ_TZ = 'Pacific/Auckland';
  const OPEN_SELECTOR = 'a[href$="#book-appointment"], [data-appointment-open]';
  const TOTAL_STEPS = 4;

  const DEFAULTS = {
    eventTypeId: '',
    site: 'retail',
    lookaheadDays: 15,
    calStylesField: 'notes',
    calPhoneField: 'attendeePhoneNumber',
    // Cal.com emails whoever is booked as the attendee. Booking a staff address instead of the customer
    // means Cal.com's emails (and the calendar invite) go to staff only; customers hear from Klaviyo.
    calNotifyEmail: '',
    calNotifyCc: '',
    klaviyoCompanyId: '',
    klaviyoListId: '',
    contactEmail: 'hello@deadlyponies.com',
    prefill: {},
    askCompany: false,
    productLinkShow: false,
    productLinkText: 'Book a virtual styling appointment',
    text: {
      drawerTitle: 'Book an Appointment', // shown in capitals by the stylesheet
      close: 'Close',
      back: 'Back',
      continue: 'Continue',

      step1Title: '1. Select a service',
      step1Subtitle: 'Discover a personalised virtual styling experience with our Ponsonby stylists.',
      serviceName: 'Virtual Styling Appointment',
      appointmentName: 'Virtual Styling Appointment',
      durationLabel: '45 minutes',

      step2Title: '2. Select a date and time',
      step2Subtitle: 'Explore available appointment times below.',
      timeLabel: 'Select your time',
      timePlaceholder: 'Choose a time',
      timeZoneNote: 'Times shown in {zone}.',
      nzLabel: 'NZ',
      zoneTime: '{zone} time',
      loadingTimes: 'Loading availabilities…',
      noTimesAtAll: 'There are no appointments available in the next two weeks. Please contact {email}.',
      timesError: 'We couldn’t load availabilities.',
      retry: 'Try again',
      slotTaken: 'Sorry, that time has just been booked. Please choose another time.',
      previousMonth: 'Previous month',
      nextMonth: 'Next month',

      step3Title: '3. Personal information',
      firstNameLabel: 'First Name',
      lastNameLabel: 'Last Name',
      companyLabel: 'Company',
      emailLabel: 'E-mail',
      phoneLabel: 'Phone',
      phoneHint: 'Please include your country code, e.g. +44 20 7946 0000',
      stylesLabel: 'Which styles are you interested in?',
      stylesPlaceholder: 'Type your response here',
      requiredError: 'Please fill in this field.',
      emailError: 'Please enter a valid email address.',

      step4Title: '4. Confirm your appointment details',
      optInLabel: 'Keep me updated on new collections, events and exclusive offers.',
      confirmButton: 'Confirm Appointment',
      confirming: 'Booking…',
      bookingError: 'Something went wrong and your appointment wasn’t booked. Please try again, or contact {email}.',

      confirmedTitle: 'Your appointment is confirmed',
      confirmedEmailNote: 'You will receive a confirmation email with your Google Meet details shortly.',
      confirmedChangeNote: 'Should you need to change or cancel your appointment, please reply to your confirmation email or contact {email}.',
      done: 'Close',
    },
  };

  /* ---------- small helpers ---------- */

  const esc = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const fill = (template, values) =>
    String(template).replace(/\{(\w+)\}/g, (m, k) => (k in values ? values[k] : m));

  const pad = (n) => String(n).padStart(2, '0');
  const dateKey = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`; // m is 1-12
  const parseKey = (k) => k.split('-').map(Number);

  const addDays = (k, n) => {
    const [y, m, d] = parseKey(k);
    const dt = new Date(Date.UTC(y, m - 1, d + n));
    return dateKey(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
  };

  const todayIn = (tz) => {
    const p = Object.fromEntries(
      new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' })
        .formatToParts(new Date())
        .map((x) => [x.type, x.value])
    );
    return `${p.year}-${p.month}-${p.day}`;
  };

  // A date key rendered as words, independent of any time zone.
  const keyLabel = (k, opts) => {
    const [y, m, d] = parseKey(k);
    return new Intl.DateTimeFormat(undefined, { ...opts, timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d)));
  };

  const fmt = (iso, tz, opts) => new Intl.DateTimeFormat(undefined, { ...opts, timeZone: tz }).format(new Date(iso));
  // Always 12-hour, compact: "2pm", "2:45pm" (whatever the visitor's device prefers).
  const fmtTime = (iso, tz) => {
    const p = Object.fromEntries(
      new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: tz })
        .formatToParts(new Date(iso))
        .map((x) => [x.type, x.value])
    );
    return `${p.hour}${p.minute === '00' ? '' : `:${p.minute}`}${String(p.dayPeriod).toLowerCase()}`;
  };
  const fmtDate = (iso, tz) => fmt(iso, tz, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const wallClock = (iso, tz) =>
    new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).format(new Date(iso));

  const zoneName = (tz, style = 'long', at = new Date()) => {
    try {
      return new Intl.DateTimeFormat(undefined, { timeZone: tz, timeZoneName: style })
        .formatToParts(at)
        .find((p) => p.type === 'timeZoneName').value;
    } catch {
      return tz;
    }
  };

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const ICON_CLOSE =
    '<svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.1" fill="none"/></svg>';
  const ICON_VIDEO =
    '<svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="6" width="14" height="12" rx="1"/><path d="M16 10l6-3v10l-6-3"/></svg>';
  const ICON_PREV =
    '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M10 4L6 8l4 4" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const ICON_NEXT =
    '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M6 4l4 4-4 4" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /* ---------- the element ---------- */

  class AppointmentDrawer extends HTMLElement {
    connectedCallback() {
      if (this.panel) return;
      // If the script runs before the page has finished parsing, the config <script> inside
      // this element may not exist yet: wait for it.
      if (!this.querySelector('script[data-appointment-config]') && document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.connectedCallback(), { once: true });
        return;
      }
      this.config = this.readConfig();
      this.t = this.config.text;
      this.tz = Intl.DateTimeFormat().resolvedOptions().timeZone || NZ_TZ;
      this.today = todayIn(this.tz);
      this.lastDay = addDays(this.today, Number(this.config.lookaheadDays) || 15);

      const p = this.config.prefill || {};
      this.state = {
        step: 1,
        serviceSelected: false, // step 1: the customer must pick the service before Continue
        slotsStatus: 'idle', // idle | loading | ready | error
        slots: new Map(), // date key -> [{ start, end }, ...]
        selectedDate: null,
        focusDate: null,
        selectedSlot: null, // ISO start
        month: this.monthOf(this.today),
        notice: '',
        form: {
          firstName: p.firstName || '',
          lastName: p.lastName || '',
          email: p.email || '',
          company: p.company || '',
          phone: p.phone || '',
          styles: '',
          optIn: false,
        },
        errors: {},
        submitting: false,
        bookingError: '',
        booking: null,
      };

      this.innerHTML = `
        <div class="apd-overlay"></div>
        <div class="apd" role="dialog" aria-modal="true" aria-labelledby="apd-title" inert>
          <div class="apd__header">
            <p class="apd__title" id="apd-title">${esc(this.t.drawerTitle)}</p>
            <button type="button" class="apd__close" data-action="close" aria-label="${esc(this.t.close)}">${ICON_CLOSE}</button>
          </div>
          <div class="apd__progress" aria-hidden="true"><span></span></div>
          <div class="apd__body"></div>
          <div class="apd__footer">
            <button type="button" class="apd__back" data-action="back">${ICON_PREV}<span>${esc(this.t.back)}</span></button>
            <button type="button" class="apd__btn" data-action="next"></button>
          </div>
          <p class="apd__sr" aria-live="polite"></p>
        </div>`;
      this.panel = this.querySelector('.apd');
      this.body = this.querySelector('.apd__body');
      this.live = this.querySelector('.apd__sr');

      this.panel.addEventListener('click', (e) => this.onClick(e));
      this.querySelector('.apd-overlay').addEventListener('click', () => this.close());
      this.panel.addEventListener('input', (e) => this.onInput(e));
      this.panel.addEventListener('change', (e) => this.onInput(e));
      this.panel.addEventListener('submit', (e) => {
        e.preventDefault();
        this.next();
      });
      // Escape and the focus trap listen on the document, so they work wherever focus is.
      this.onDocKeydown = (e) => this.onKeydown(e);
      document.addEventListener('keydown', this.onDocKeydown);

      this.render();
      this.fillProductLinks();
      if (location.hash === '#book-appointment') this.open();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onDocKeydown);
    }

    // The product page renders an empty slot for visitors outside NZ
    // (snippets/appointment-trigger.liquid); fill it if the section setting is on.
    fillProductLinks() {
      if (!this.config.productLinkShow || !this.config.productLinkText) return;
      document.querySelectorAll('[data-appointment-product-link]').forEach((slot) => {
        slot.innerHTML = `<a href="#book-appointment" class="appointment-trigger">${ICON_VIDEO}<span>${esc(this.config.productLinkText)}</span></a>`;
        slot.hidden = false;
      });
    }

    readConfig() {
      let raw = {};
      const node = this.querySelector('script[data-appointment-config]');
      try {
        raw = node ? JSON.parse(node.textContent) : {};
      } catch (e) {
        console.error('[appointment-drawer] invalid config JSON', e);
      }
      const text = { ...DEFAULTS.text };
      for (const [k, v] of Object.entries(raw.text || {})) if (v !== null && v !== '') text[k] = v;
      return { ...DEFAULTS, ...raw, text };
    }

    monthOf(k) {
      const [y, m] = parseKey(k);
      return { y, m };
    }

    /* ---------- open / close ---------- */

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(trigger) {
      if (this.isOpen) return;
      this.returnFocus = trigger || document.activeElement;
      this.classList.add('is-open');
      this.panel.removeAttribute('inert');
      document.documentElement.classList.add('apd-lock');
      // A finished booking starts fresh next time.
      if (this.state.step === 5) this.reset();
      // Always fetch fresh times on open, so blocks made in Cal.com or the calendar show straight away.
      this.loadSlots();
      this.render();
      requestAnimationFrame(() => this.focusHeading());
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.panel.setAttribute('inert', '');
      document.documentElement.classList.remove('apd-lock');
      if (location.hash === '#book-appointment') history.replaceState(null, '', location.pathname + location.search);
      if (this.returnFocus && document.contains(this.returnFocus)) this.returnFocus.focus();
    }

    reset() {
      Object.assign(this.state, {
        step: 1, serviceSelected: false, selectedSlot: null, notice: '', errors: {}, bookingError: '', booking: null, submitting: false, slotsStatus: 'idle',
      });
      Object.assign(this.state.form, { styles: '', optIn: false });
    }

    goTo(step) {
      this.state.step = step;
      this.state.bookingError = '';
      this.render();
      this.focusHeading();
      this.body.scrollTop = 0;
    }

    focusHeading() {
      const h = this.body.querySelector('[data-heading]');
      if (h) h.focus({ preventScroll: true });
    }

    // The footer's main button: what "Continue" means on each step.
    next() {
      const s = this.state;
      if (s.step === 1) {
        if (s.serviceSelected) this.goTo(2);
      } else if (s.step === 2) {
        if (s.selectedSlot) this.goTo(3);
      } else if (s.step === 3) {
        if (this.validate()) this.goTo(4);
      } else if (s.step === 4) this.confirmBooking();
      else this.close();
    }

    /* ---------- Cal.com ---------- */

    async loadSlots() {
      const s = this.state;
      const token = (this.slotsToken = (this.slotsToken || 0) + 1);
      s.slotsStatus = 'loading';
      this.render();

      const q = new URLSearchParams({
        eventTypeId: String(this.config.eventTypeId),
        start: new Date().toISOString(),
        end: new Date(Date.now() + (Number(this.config.lookaheadDays) || 15) * 864e5).toISOString(),
        timeZone: this.tz,
        format: 'range',
      });

      try {
        const res = await fetch(`${CAL_API}/slots?${q}`, { headers: { 'cal-api-version': SLOTS_VERSION } });
        if (!res.ok) throw new Error(`slots ${res.status}`);
        const json = await res.json();
        if (token !== this.slotsToken) return;
        const entries = Object.entries(json.data || {})
          .map(([k, list]) => [k, (list || []).map((x) => ({ start: x.start || x.time, end: x.end || null })).filter((x) => x.start)])
          .filter(([, list]) => list.length)
          .sort(([a], [b]) => (a < b ? -1 : 1));
        s.slots = new Map(entries);
        s.slotsStatus = 'ready';
        if (!s.selectedDate || !s.slots.has(s.selectedDate)) {
          s.selectedDate = entries.length ? entries[0][0] : null;
        }
        if (s.selectedSlot && !this.slotStillFree(s.selectedSlot)) s.selectedSlot = null;
        s.focusDate = s.selectedDate || this.today;
        s.month = this.monthOf(s.focusDate);
      } catch (e) {
        if (token !== this.slotsToken) return;
        console.error('[appointment-drawer]', e);
        s.slotsStatus = 'error';
      }
      this.render();
    }

    // Cal.com writes the same instant as "…Z" or "…+13:00", so compare instants, not strings.
    findSlot(iso) {
      const at = new Date(iso).getTime();
      for (const list of this.state.slots.values()) {
        const hit = list.find((x) => new Date(x.start).getTime() === at);
        if (hit) return hit;
      }
      return null;
    }

    slotStillFree(iso) {
      return !!this.findSlot(iso);
    }

    slotEnd(iso) {
      const hit = this.findSlot(iso);
      return hit ? hit.end : null;
    }

    async confirmBooking() {
      const s = this.state;
      if (s.submitting || !s.selectedSlot) return;
      s.submitting = true;
      s.bookingError = '';
      this.render();

      const f = s.form;
      const name = `${f.firstName} ${f.lastName}`.trim();
      const responses = {};
      // Company (B2B) goes at the top of the booking notes so the host sees which store is calling.
      const staffEmail = String(this.config.calNotifyEmail || '').trim();
      const staffCc = String(this.config.calNotifyCc || '').split(/[\s,;]+/).filter((e) => EMAIL_RE.test(e));
      const toStaff = EMAIL_RE.test(staffEmail);
      // When Cal.com is emailing staff, the customer's contact details lead the notes instead.
      const notes = [
        toStaff && `Customer: ${name}`,
        toStaff && `Email: ${f.email}`,
        toStaff && f.phone && `Phone: ${f.phone}`,
        f.company && `Company: ${f.company}`,
        f.styles && (f.company || toStaff ? `Styles: ${f.styles}` : f.styles),
      ]
        .filter(Boolean)
        .join('\n');
      if (this.config.calStylesField && notes) responses[this.config.calStylesField] = notes;
      // Cal.com's phone question only accepts international format, so only send it when we can.
      const phone = this.internationalPhone(f.phone);
      if (this.config.calPhoneField && phone) responses[this.config.calPhoneField] = phone;

      const body = {
        start: new Date(s.selectedSlot).toISOString(),
        eventTypeId: Number(this.config.eventTypeId),
        attendee: toStaff
          ? { name, email: staffEmail, timeZone: NZ_TZ, language: 'en' }
          : { name, email: f.email, timeZone: this.tz, language: (document.documentElement.lang || 'en').slice(0, 2) },
        ...(toStaff && staffCc.length ? { guests: staffCc } : {}),
        bookingFieldsResponses: responses,
        metadata: {
          phone: f.phone.slice(0, 500),
          styles: f.styles.slice(0, 500),
          company: f.company.slice(0, 500),
          customerEmail: f.email.slice(0, 500),
          site: String(this.config.site),
          marketingOptIn: f.optIn ? 'yes' : 'no',
          source: 'shopify-appointment-drawer',
        },
      };

      const send = async () => {
        try {
          const r = await fetch(`${CAL_API}/bookings`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'cal-api-version': BOOKINGS_VERSION },
            body: JSON.stringify(body),
          });
          return [r, await r.json().catch(() => null)];
        } catch (e) {
          console.error('[appointment-drawer] booking request failed', e);
          return [undefined, null];
        }
      };

      let [res, json] = await send();

      // A rejected phone number must never cost the booking: retry once without it
      // (it's still saved in the booking's metadata).
      if (res && res.status === 400 && this.config.calPhoneField in responses) {
        console.warn('[appointment-drawer] retrying without phone field', json);
        delete responses[this.config.calPhoneField];
        [res, json] = await send();
      }

      if (res && res.ok && json && json.data && json.data.uid) {
        s.booking = json.data;
        s.submitting = false;
        s.slotsStatus = 'idle'; // times have changed; reload next time
        this.tellKlaviyo(json.data);
        this.goTo(5);
        this.live.textContent = this.t.confirmedTitle;
        return;
      }

      console.error('[appointment-drawer] booking failed', res && res.status, json);

      // Was the time taken in the meantime? Re-read availability and check.
      if (res) {
        const chosen = s.selectedSlot;
        await this.loadSlots();
        if (s.slotsStatus === 'ready' && !this.slotStillFree(chosen)) {
          s.submitting = false;
          s.selectedSlot = null;
          s.notice = this.t.slotTaken;
          this.goTo(2);
          this.live.textContent = this.t.slotTaken;
          return;
        }
      }

      s.submitting = false;
      s.bookingError = this.t.bookingError;
      this.render();
      this.live.textContent = this.t.bookingError;
      this.body.querySelector('.apd__error')?.focus();
    }

    /* ---------- Klaviyo (never blocks or fails the booking) ---------- */

    tellKlaviyo(booking) {
      const companyId = this.config.klaviyoCompanyId;
      if (!companyId) return;
      const f = this.state.form;
      const start = booking.start || this.state.selectedSlot;
      const post = (path, data) =>
        fetch(`${KLAVIYO_API}/${path}?company_id=${encodeURIComponent(companyId)}`, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
          body: JSON.stringify({ data }),
        }).catch((e) => console.warn('[appointment-drawer] Klaviyo', path, e));

      try {
        post('events', {
          type: 'event',
          attributes: {
            metric: { data: { type: 'metric', attributes: { name: 'Booked Appointment' } } },
            profile: {
              data: { type: 'profile', attributes: { email: f.email, first_name: f.firstName, last_name: f.lastName } },
            },
            unique_id: booking.uid,
            properties: {
              FirstName: f.firstName,
              AppointmentName: this.t.appointmentName,
              Site: this.config.site === 'b2b' ? 'B2B' : 'Retail',
              StartTimeCustomer: `${fmtDate(start, this.tz)}, ${fmtTime(start, this.tz)} (${this.zoneLabel(start)})`,
              CustomerTimeZone: this.tz,
              StartTimeNZ: `${fmtDate(start, NZ_TZ)}, ${fmtTime(start, NZ_TZ)} (NZ)`,
              StartTimeISO: new Date(start).toISOString(),
              Duration: this.t.durationLabel,
              MeetingURL: booking.location || booking.meetingUrl || '',
              BookingReference: booking.uid,
              Phone: f.phone,
              StylesOfInterest: f.styles,
              Company: f.company,
              MarketingOptIn: f.optIn,
            },
          },
        });

        if (f.optIn && this.config.klaviyoListId) {
          post('subscriptions', {
            type: 'subscription',
            attributes: {
              custom_source: 'Appointment booking drawer',
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email: f.email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } },
                },
              },
            },
            relationships: { list: { data: { type: 'list', id: this.config.klaviyoListId } } },
          });
        }
      } catch (e) {
        console.warn('[appointment-drawer] Klaviyo', e);
      }
    }

    /* ---------- events ---------- */

    onClick(e) {
      const el = e.target.closest('[data-action]');
      if (!el || !this.panel.contains(el) || el.getAttribute('aria-disabled') === 'true' || el.disabled) return;
      const s = this.state;
      switch (el.dataset.action) {
        case 'close':
          this.close();
          break;
        case 'back':
          if (!s.submitting && s.step > 1 && s.step < 5) this.goTo(s.step - 1);
          break;
        case 'next':
          this.next();
          break;
        case 'service':
          s.serviceSelected = true;
          this.render();
          break;
        case 'retry':
          this.loadSlots();
          break;
        case 'month':
          this.shiftMonth(Number(el.dataset.dir));
          break;
        case 'day':
          this.pickDay(el.dataset.date);
          break;
      }
    }

    onInput(e) {
      const el = e.target;
      if (el.name === 'slot') {
        this.state.selectedSlot = el.value || null;
        this.state.notice = '';
        this.renderFooter();
        return;
      }
      if (!el.name || !(el.name in this.state.form)) return;
      this.state.form[el.name] = el.type === 'checkbox' ? el.checked : el.value;
      if (this.state.errors[el.name]) {
        delete this.state.errors[el.name];
        el.removeAttribute('aria-invalid');
        el.closest('.apd-field')?.classList.remove('has-error');
        const msg = this.body.querySelector(`#apd-err-${el.name}`);
        if (msg) msg.textContent = '';
      }
    }

    validate() {
      const f = this.state.form;
      for (const k of ['firstName', 'lastName', 'email', 'phone', 'styles', 'company']) f[k] = f[k].trim();
      const errors = {};
      for (const k of ['firstName', 'lastName', 'email', 'phone']) if (!f[k]) errors[k] = this.t.requiredError;
      if (f.email && !EMAIL_RE.test(f.email)) errors.email = this.t.emailError;
      this.state.errors = errors;
      if (Object.keys(errors).length) {
        this.render();
        this.body.querySelector('[aria-invalid="true"]')?.focus();
        return false;
      }
      return true;
    }

    onKeydown(e) {
      if (!this.isOpen) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
        return;
      }
      if (e.key === 'Tab') {
        const items = [...this.panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        const inside = this.panel.contains(document.activeElement);
        if (!inside) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
        } else if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
        return;
      }
      const day = e.target.closest && e.target.closest('[data-action="day"]');
      if (day) this.onCalendarKey(e, day.dataset.date);
    }

    /* ---------- calendar ---------- */

    monthBounds() {
      return { min: this.monthOf(this.today), max: this.monthOf(this.lastDay) };
    }

    shiftMonth(dir) {
      const { min, max } = this.monthBounds();
      let { y, m } = this.state.month;
      m += dir;
      if (m < 1) { m = 12; y -= 1; }
      if (m > 12) { m = 1; y += 1; }
      const n = y * 12 + m;
      if (n < min.y * 12 + min.m || n > max.y * 12 + max.m) return;
      this.state.month = { y, m };
      // Keep keyboard focus inside the shown month.
      const firstFree = [...this.state.slots.keys()].find((k) => k.startsWith(`${y}-${pad(m)}`));
      this.state.focusDate = firstFree || dateKey(y, m, 1);
      this.render();
      this.body.querySelector(`[data-action="month"][data-dir="${dir}"]`)?.focus();
    }

    pickDay(k) {
      const s = this.state;
      if (!s.slots.has(k)) return;
      if (s.selectedDate !== k) s.selectedSlot = null;
      s.selectedDate = k;
      s.focusDate = k;
      s.notice = '';
      this.render();
      this.body.querySelector(`[data-date="${k}"]`)?.focus();
      this.live.textContent = keyLabel(k, { weekday: 'long', day: 'numeric', month: 'long' });
    }

    onCalendarKey(e, k) {
      const moves = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
      let next = null;
      if (e.key in moves) next = addDays(k, moves[e.key]);
      else if (e.key === 'Home' || e.key === 'End') {
        const [y, m, d] = parseKey(k);
        const dow = (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
        next = addDays(k, e.key === 'Home' ? -dow : 6 - dow);
      } else if (e.key === 'PageUp' || e.key === 'PageDown') {
        e.preventDefault();
        this.shiftMonth(e.key === 'PageUp' ? -1 : 1);
        return;
      } else return;

      e.preventDefault();
      const { min, max } = this.monthBounds();
      const minKey = dateKey(min.y, min.m, 1);
      const maxKey = addDays(dateKey(max.m === 12 ? max.y + 1 : max.y, max.m === 12 ? 1 : max.m + 1, 1), -1);
      if (next < minKey || next > maxKey) return;
      this.state.focusDate = next;
      this.state.month = this.monthOf(next);
      this.render();
      this.body.querySelector(`[data-date="${next}"]`)?.focus();
    }

    /* ---------- rendering ---------- */

    render() {
      const s = this.state;
      this.classList.toggle('is-confirmed', s.step === 5);
      this.querySelector('.apd__progress span').style.width = `${(Math.min(s.step, TOTAL_STEPS) / TOTAL_STEPS) * 100}%`;

      // Re-rendering replaces the step's markup, so put focus back on the equivalent element.
      const restore = this.focusKey(document.activeElement);
      const view = [null, this.viewService, this.viewDateTime, this.viewDetails, this.viewConfirm, this.viewConfirmed][s.step];
      this.body.innerHTML = view.call(this);
      if (restore) this.body.querySelector(restore)?.focus({ preventScroll: true });
      this.renderFooter();
    }

    renderFooter() {
      const s = this.state;
      const t = this.t;
      const back = this.querySelector('.apd__back');
      const btn = this.querySelector('.apd__btn');
      back.hidden = s.step === 1 || s.step === 5;
      back.disabled = s.submitting;
      let label = t.continue;
      let disabled = false;
      if (s.step === 1) disabled = !s.serviceSelected;
      if (s.step === 2) disabled = !s.selectedSlot;
      if (s.step === 4) {
        label = s.submitting ? t.confirming : t.confirmButton;
        disabled = s.submitting;
      }
      if (s.step === 5) label = t.done;
      btn.textContent = label;
      btn.disabled = disabled;
      btn.toggleAttribute('aria-busy', s.submitting);
      btn.classList.toggle('apd__btn--ready', !disabled && s.step > 1 && s.step < 5);
    }

    focusKey(el) {
      if (!el || !this.body.contains(el)) return null;
      if (el.hasAttribute('data-heading')) return '[data-heading]';
      if (el.id) return `#${CSS.escape(el.id)}`;
      if (el.dataset.date) return `[data-date="${el.dataset.date}"]`;
      if (el.dataset.action) return `[data-action="${el.dataset.action}"]`;
      return null;
    }

    mailto() {
      const e = esc(this.config.contactEmail);
      return `<a href="mailto:${e}">${e}</a>`;
    }

    // "+64 21 555 0100" → "+64215550100"; NZ visitors' "021 555 0100" → "+64215550100".
    // Returns null when the number can't safely be put in international format.
    internationalPhone(raw) {
      let p = String(raw || '').replace(/[^\d+]/g, '');
      if (p.startsWith('00')) p = `+${p.slice(2)}`;
      if (!p.startsWith('+') && p.startsWith('0') && this.tz === NZ_TZ) p = `+64${p.slice(1)}`;
      return /^\+[1-9]\d{7,14}$/.test(p) ? p : null;
    }

    // "NZ time", a familiar abbreviation like "BST" / "AEDT", or else the city: "London time".
    zoneLabel(iso) {
      if (this.tz === NZ_TZ) return fill(this.t.zoneTime, { zone: this.t.nzLabel });
      const short = zoneName(this.tz, 'short', new Date(iso));
      if (!/^(GMT|UTC)/.test(short)) return short;
      return fill(this.t.zoneTime, { zone: this.tz.split('/').pop().replace(/_/g, ' ') });
    }

    showNz(iso) {
      return this.tz !== NZ_TZ && wallClock(iso, this.tz) !== wallClock(iso, NZ_TZ);
    }

    nzTime(iso) {
      const o = { year: 'numeric', month: 'numeric', day: 'numeric' };
      const day = fmt(iso, this.tz, o) === fmt(iso, NZ_TZ, o) ? '' : `${fmt(iso, NZ_TZ, { weekday: 'short', day: 'numeric', month: 'short' })}, `;
      return `${day}${fmtTime(iso, NZ_TZ)} ${this.t.nzLabel}`;
    }

    timeRange(iso) {
      const end = this.slotEnd(iso);
      return end ? `${fmtTime(iso, this.tz)} – ${fmtTime(end, this.tz)}` : fmtTime(iso, this.tz);
    }

    stepHead(title, subtitle) {
      return `<h2 class="apd-step__title" tabindex="-1" data-heading>${esc(title)}</h2>
        ${subtitle ? `<p class="apd-step__subtitle">${subtitle}</p>` : ''}`;
    }

    viewService() {
      const t = this.t;
      return `
        ${this.stepHead(t.step1Title, esc(t.step1Subtitle))}
        <ul class="apd-options">
          <li>
            <button type="button" class="apd-option${this.state.serviceSelected ? ' is-selected' : ''}" data-action="service" aria-pressed="${this.state.serviceSelected}">
              <span>${esc(t.serviceName)}</span>
              <span class="apd-option__meta">${esc(t.durationLabel)}</span>
            </button>
          </li>
        </ul>`;
    }

    viewDateTime() {
      const s = this.state;
      const t = this.t;
      let content;

      if (s.slotsStatus === 'loading' || s.slotsStatus === 'idle') {
        content = `<p class="apd__status" role="status">${esc(t.loadingTimes)}</p>`;
      } else if (s.slotsStatus === 'error') {
        content = `<div class="apd__error" role="alert" tabindex="-1">${esc(t.timesError)}
          <button type="button" class="apd__link" data-action="retry">${esc(t.retry)}</button></div>`;
      } else if (!s.slots.size) {
        content = `<p class="apd__status">${fill(esc(t.noTimesAtAll), { email: this.mailto() })}</p>`;
      } else {
        content = `${this.viewCalendar()}${this.viewTimes()}`;
      }

      return `
        ${this.stepHead(t.step2Title, esc(t.step2Subtitle))}
        ${s.notice ? `<p class="apd__error" role="alert">${esc(s.notice)}</p>` : ''}
        ${content}`;
    }

    viewCalendar() {
      const s = this.state;
      const { y, m } = s.month;
      const { min, max } = this.monthBounds();
      const n = y * 12 + m;
      const atMin = n <= min.y * 12 + min.m;
      const atMax = n >= max.y * 12 + max.m;
      const first = new Date(Date.UTC(y, m - 1, 1));
      const lead = (first.getUTCDay() + 6) % 7; // Monday first
      const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
      const monthName = new Intl.DateTimeFormat(undefined, { month: 'long', timeZone: 'UTC' }).format(first);
      const monthTitle = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(first);

      // Monday-first two-letter weekdays ("Mo", "Tu"…). 2024-01-01 was a Monday.
      const weekdays = Array.from({ length: 7 }, (_, i) => {
        const long = keyLabel(addDays('2024-01-01', i), { weekday: 'long' });
        return { short: long.slice(0, 2), long };
      });

      const cells = [];
      for (let i = 0; i < lead; i++) cells.push('<td></td>');
      for (let d = 1; d <= days; d++) {
        const k = dateKey(y, m, d);
        const free = s.slots.has(k);
        const selected = k === s.selectedDate;
        const focusable = k === s.focusDate || (!s.focusDate && d === 1);
        cells.push(`<td><button type="button" class="apd-cal__day${free ? ' is-free' : ''}${selected ? ' is-selected' : ''}"
          data-action="day" data-date="${k}" tabindex="${focusable ? 0 : -1}"
          aria-label="${esc(keyLabel(k, { weekday: 'long', day: 'numeric', month: 'long' }))}"
          ${selected ? 'aria-pressed="true"' : free ? 'aria-pressed="false"' : 'aria-disabled="true"'}>${d}</button></td>`);
      }
      while (cells.length % 7) cells.push('<td></td>');
      const rows = [];
      for (let i = 0; i < cells.length; i += 7) rows.push(`<tr>${cells.slice(i, i + 7).join('')}</tr>`);

      return `
        <div class="apd-cal">
          <div class="apd-cal__head">
            <p class="apd-cal__month" aria-live="polite"><span class="apd__sr">${esc(monthTitle)}</span><span aria-hidden="true">${esc(monthName)}</span></p>
            <div class="apd-cal__nav">
              <button type="button" data-action="month" data-dir="-1" aria-label="${esc(this.t.previousMonth)}" ${atMin ? 'aria-disabled="true"' : ''}>${ICON_PREV}</button>
              <button type="button" data-action="month" data-dir="1" aria-label="${esc(this.t.nextMonth)}" ${atMax ? 'aria-disabled="true"' : ''}>${ICON_NEXT}</button>
            </div>
          </div>
          <table class="apd-cal__grid" role="grid" aria-label="${esc(monthTitle)}">
            <thead><tr>${weekdays.map((w) => `<th scope="col" abbr="${esc(w.long)}">${esc(w.short)}</th>`).join('')}</tr></thead>
            <tbody>${rows.join('')}</tbody>
          </table>
        </div>`;
    }

    viewTimes() {
      const s = this.state;
      const t = this.t;
      if (!s.selectedDate) return '';
      const list = s.slots.get(s.selectedDate) || [];
      const sample = list[0] && list[0].start;
      return `
        <div class="apd-field apd-field--select">
          <label for="apd-slot" class="apd-step__title">${esc(t.timeLabel)}</label>
          <select id="apd-slot" name="slot" required>
            <option value="">${esc(t.timePlaceholder)}</option>
            ${list
              .map(({ start }) => {
                const nz = this.showNz(start) ? ` (${this.nzTime(start)})` : '';
                return `<option value="${esc(start)}" ${start === s.selectedSlot ? 'selected' : ''}>${esc(this.timeRange(start) + nz)}</option>`;
              })
              .join('')}
          </select>
          ${sample ? `<p class="apd-field__hint">${esc(fill(t.timeZoneNote, { zone: this.zoneLabel(sample) }))}</p>` : ''}
        </div>`;
    }

    field(name, label, type, autocomplete, { required = true, placeholder = '', hint = '' } = {}) {
      const s = this.state;
      const err = s.errors[name];
      const id = `apd-${name}`;
      const shown = required ? `${label} *` : label;
      if (type === 'textarea') {
        return `
          <div class="apd-field apd-field--area${err ? ' has-error' : ''}">
            <label for="${id}">${esc(shown)}</label>
            <textarea id="${id}" name="${name}" rows="3" placeholder="${esc(placeholder)}" aria-describedby="apd-err-${name}">${esc(s.form[name])}</textarea>
            <p class="apd-field__error" id="apd-err-${name}">${err ? esc(err) : ''}</p>
          </div>`;
      }
      // Khaite-style underlined line: the label sits on the line as the placeholder,
      // and stays readable (and labelled for screen readers) once typed into.
      return `
        <div class="apd-field apd-field--line${err ? ' has-error' : ''}${s.form[name] ? ' is-filled' : ''}">
          <input id="${id}" name="${name}" type="${type}" autocomplete="${autocomplete}" value="${esc(s.form[name])}" placeholder=" "
            ${required ? 'required aria-required="true"' : ''} ${err ? 'aria-invalid="true"' : ''} aria-describedby="${hint ? `${id}-hint ` : ''}apd-err-${name}">
          <label for="${id}">${esc(shown)}</label>
          ${hint ? `<p class="apd-field__hint" id="${id}-hint">${esc(hint)}</p>` : ''}
          <p class="apd-field__error" id="apd-err-${name}">${err ? esc(err) : ''}</p>
        </div>`;
    }

    viewDetails() {
      const t = this.t;
      return `
        ${this.stepHead(t.step3Title)}
        <form class="apd-form" novalidate>
          ${this.field('firstName', t.firstNameLabel, 'text', 'given-name')}
          ${this.field('lastName', t.lastNameLabel, 'text', 'family-name')}
          ${this.config.askCompany ? this.field('company', t.companyLabel, 'text', 'organization', { required: false }) : ''}
          ${this.field('email', t.emailLabel, 'email', 'email')}
          ${this.field('phone', t.phoneLabel, 'tel', 'tel', { hint: this.tz === NZ_TZ ? '' : t.phoneHint })}
          ${this.field('styles', t.stylesLabel, 'textarea', 'off', { required: false, placeholder: t.stylesPlaceholder })}
          <button type="submit" hidden></button>
        </form>`;
    }

    summary() {
      const s = this.state;
      const f = s.form;
      const iso = (s.booking && s.booking.start) || s.selectedSlot;
      const time = `${this.timeRange(iso)} ${this.zoneLabel(iso)}${this.showNz(iso) ? ` (${this.nzTime(iso)})` : ''}`;
      const lines = [
        this.t.appointmentName,
        fmtDate(iso, this.tz),
        time,
      ];
      if (s.step === 4) lines.push(`${f.firstName} ${f.lastName}`.trim(), ...(f.company ? [f.company] : []), f.email, f.phone, ...(f.styles ? [f.styles] : []));
      return `<ul class="apd-summary">${lines.map((l) => `<li>${esc(l)}</li>`).join('')}</ul>`;
    }

    viewConfirm() {
      const s = this.state;
      const t = this.t;
      return `
        ${this.stepHead(t.step4Title)}
        ${this.summary()}
        <div class="apd-check">
          <input id="apd-optIn" name="optIn" type="checkbox" ${s.form.optIn ? 'checked' : ''}>
          <label for="apd-optIn">${esc(t.optInLabel)}</label>
        </div>
        ${s.bookingError ? `<p class="apd__error" role="alert" tabindex="-1">${fill(esc(s.bookingError), { email: this.mailto() })}</p>` : ''}`;
    }

    viewConfirmed() {
      const t = this.t;
      return `
        ${this.stepHead(t.confirmedTitle)}
        ${this.summary()}
        <p class="apd-step__text">${esc(t.confirmedEmailNote)}</p>
        <p class="apd-step__text">${fill(esc(t.confirmedChangeNote), { email: this.mailto() })}</p>`;
    }
  }

  customElements.define('appointment-drawer', AppointmentDrawer);

  // One document-level listener opens the drawer from any trigger (footer menu item,
  // product page link, or anything with data-appointment-open).
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest(OPEN_SELECTOR);
    if (!trigger) return;
    const drawer = document.querySelector('appointment-drawer');
    if (!drawer) return;
    e.preventDefault();

    // Opened from a slide-out menu (e.g. the B2B theme's side navigation, a Dawn
    // <header-drawer>)? Close that menu first, and return focus to its toggle afterwards.
    let returnTo = trigger;
    // Prefer the theme's own menu element (its close also unlocks scrolling and focus).
    const menu = trigger.closest('header-drawer, menu-drawer') || trigger.closest('details[open]');
    if (menu) {
      const summary = menu.querySelector('summary');
      if (typeof menu.closeMenuDrawer === 'function') menu.closeMenuDrawer(e, summary);
      else if (menu.matches('details[open]')) menu.removeAttribute('open');
      if (summary) returnTo = summary;
    }
    drawer.open(returnTo);
  });
})();